import React, { useState, createContext, useEffect, ReactNode, useRef } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { Snackbar } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import Draggable from 'react-draggable';
import { useTranslation } from "react-i18next";
import * as Hammer from "hammerjs";

import { DataLoader } from '../';
import Settings from '../../data/settings';
import { useCookiesManager, CookiePreferences } from '../';
import { Access } from '../auth';

const isTouchDevice = () => {
  return (('ontouchstart' in window) || (navigator.maxTouchPoints > 0));
}

export enum DialogModesEnum {
  standard,
  confirmation
}

export enum ConfirmationModesEnum {
  yesNo,
  cancelSearch
}

export interface IDialog {
  open?: boolean | null;
  title?: React.ReactNode | null;
  content?: ReactNode;
  mode?: DialogModesEnum;
  modal?: boolean;
  onLoading?: () => Promise<void> | (() => void) | undefined;
  onConfirm?: () => Promise<void> | (() => void) | undefined;
  onCancel?: () => Promise<void> | (() => void) | undefined;
  confirmationMode?: ConfirmationModesEnum;
  className?: string | undefined;
}

export interface IDialogActions {
  show(d: IDialog): void;
  hide: () => void;
  showBackdrop: () => void;
  hideBackdrop: () => void;
  isOpen: () => boolean;
  isLoading: () => boolean;
  setIsLoading: (isLoading: boolean) => void;
  setClassName: (className: string) => void;
  setContent: (content: ReactNode | null) => void;
}

export enum ToastSeverityEnum {
  success = "success",
  error = "error",
  info = "info",
  warning = "warning"
}

export interface IToast {
  open: boolean | undefined;
  content: ReactNode | null;
  severity: ToastSeverityEnum | undefined;
  autoHideDuration?: number | null
}

export interface IToastNotificationActions {
  show(t: IToast): void
  hide: () => void;
}

export interface ILayoutContext {
  dialog: IDialogActions;
  toast: IToastNotificationActions;
  onAccess: () => void;
}

export const LayoutContext = createContext<ILayoutContext | null>(null);

function PaperComponent(props: any) {
  return (
    <Draggable handle='#draggable-dialog-title' cancel={'[class*="actions"]'}>
      <Paper {...props} />
    </Draggable>
  );
}

export interface LayoutToolsProps {
  title?: string;
  children?: any;
  onCookiesAccepted?: (p?: CookiePreferences) => void;
  onCookiesLoaded?: (p?: CookiePreferences) => void;
}

const LayoutTools = (props: LayoutToolsProps) => {
  const { t } = useTranslation();

  const dialogRef = useRef<IDialog | null>();
  const [dialog, _setDialog] = useState<IDialog | null>(null);
  const setDialog = (d: IDialog | null) => {
    _setDialog(d);
    dialogRef.current = d;
  }

  const [isBackdropVisible, setIsBackdropVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState<ReactNode>(null);
  const [dialogClassName, setDialogClassName] = useState<string>('');
  const [dialogLoadingStatus, setDialogLoadingStatus] = useState(-1); // -1 not loading, 0 requested loading, 1 actually loading

  const [toast, setToast] = useState<IToast | null>({
    open: false,
    content: '',
    severity: undefined,
    autoHideDuration: null
  });

  const [, setTitle] = useState("");

  useEffect(() => {
    setTitle(Settings.project.title);

    const enablePagesSwipe = false;

    if (enablePagesSwipe) {
      // * Experimental *

      const element = document.getElementById("layout");

      if (element) {
        const mc = new Hammer.Manager(element);

        mc.add(new Hammer.Swipe({ event: 'swipe', pointers: isTouchDevice() ? 2 : 1 }));

        mc.get('swipe').set({ direction: Hammer.DIRECTION_HORIZONTAL });

        mc.on("swipe", (e) => {
          console.log("swipe " + e.direction);

          showToast({
            open: true,
            content: "swipe " + e.direction,
            severity: ToastSeverityEnum.info
          });
        });
      }
    }

    const effectDialogLoading = async () => {
      if (dialogLoadingStatus === 0) {
        setDialogLoadingStatus(1);
        console.log("effect effectDialogLoading", dialog)

        if (dialog?.onLoading)
          await dialog.onLoading();

        setDialogLoadingStatus(-1);
      }
    }

    effectDialogLoading();
  }, [dialog, dialogLoadingStatus]);

  const showDialog = (d: IDialog) => {
    if (!d.open) {
      d.open = true;

      setDialogLoadingStatus(0); // Request onLoading
      setDialog(d);
    }
  };

  const hideDialog = () => {
    if (!dialog || !dialog.modal)
      setDialog({ ...dialog, open: false });
  };

  const showBackdrop = () => {
    setIsBackdropVisible(true);
  };

  const hideBackdrop = () => {
    setIsBackdropVisible(false);
  };

  const isDialogOpen = () => (dialog && dialog.open) ? true : false;

  const showToast = (t: IToast) => {
    setToast(t);
  }

  const hideToast = () => {
    setToast({ ...toast, open: false } as IToast)
  }

  const onAccess = () => {
    showDialog({
      title: <><i className="fas fa-user"></i> {t('account-login')}</>,
      content: (<Access enableMagicLink onSignedIn={hideDialog}
        onMagicLinkRequested={hideDialog} />)
    })
  }

  const LayoutToastNotification = () => {
    return (
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        open={toast?.open}
        onClose={hideToast}
        autoHideDuration={toast?.autoHideDuration}>
        <Alert severity={toast?.severity} sx={{ width: '100%' }}>
          {toast?.content}
        </Alert>
      </Snackbar>);
  }

  const appContext: ILayoutContext = {
    dialog: {
      show: showDialog,
      hide: hideDialog,
      showBackdrop: showBackdrop,
      hideBackdrop: hideBackdrop,
      isOpen: isDialogOpen,
      setClassName: setDialogClassName,
      setContent: setDialogContent,
      isLoading: () => dialogLoadingStatus > -1,
      setIsLoading: (isLoading: boolean) =>
        setDialogLoadingStatus(isLoading ? 0 : -1)
    },
    toast: {
      show: showToast,
      hide: hideToast
    },
    onAccess: onAccess
  };

  useCookiesManager(appContext, props.onCookiesAccepted, props.onCookiesLoaded);

  const LayoutDialog = () => {
    if (!dialog)
      return null;

    return (
      <Dialog
        className={`layout-dialog ${dialogClassName}`}
        open={dialog.open === true}
        onClose={hideDialog}
        PaperComponent={PaperComponent}
        aria-labelledby='draggable-dialog-title'>
        <Box className="dialog-box">
          {dialog.title && (
            <div className="header" id='draggable-dialog-title'>
              <div className="content">
                {dialog.title}
              </div>

              <div className="actions">
                {!dialog.modal &&
                  <IconButton color="primary" aria-label="close dialog"
                    onClick={hideDialog}>
                    <CloseIcon />
                  </IconButton>}
              </div>
            </div>
          )}

          <DialogContent style={{ overflow: "visible" }}>
            {dialogContent}
          </DialogContent>

          {dialog.mode === DialogModesEnum.confirmation && (
            <DialogActions>
              <Button autoFocus onClick={async () => {
                if (dialog.onCancel)
                  await dialog.onCancel();

                hideDialog();
              }}>
                {dialog.confirmationMode === ConfirmationModesEnum.cancelSearch
                  ? t('cancel')
                  : t('no')}
              </Button>

              <Button
                variant='contained'
                startIcon={dialog.confirmationMode === ConfirmationModesEnum.cancelSearch ? <SearchIcon /> : undefined}
                onClick={async () => {
                  if (dialog.onConfirm)
                    await dialog.onConfirm();

                  setDialog({ ...dialog, open: false });
                }}>
                {dialog.confirmationMode === ConfirmationModesEnum.cancelSearch
                  ? t('search')
                  : t('yes')}
              </Button>
            </DialogActions>
          )}
        </Box>

        {(isBackdropVisible || dialogLoadingStatus > -1) &&
          <div className="dialog-backdrop backdrop">
            <DataLoader title={props.title} />
          </div>}
      </Dialog>
    );
  }

  return (
    <>
      <LayoutContext.Provider value={appContext}>
        {props.children}
      </LayoutContext.Provider>

      {dialog && <LayoutDialog />}
      {toast && <LayoutToastNotification />}
    </>
  );
};

export default LayoutTools;
